import { createAction, createSlice } from "@reduxjs/toolkit";
// Types
import { Category } from "../../@types/Category";
import { Document } from "../../@types/document";
import { Status } from "../../@types/status";
import { fetchDocuments } from "./actions/fetchDocuments";
import { signDocument } from "./actions/signDocument";
// Actions

interface SignerState {
  documents: Document[];
  searchText: string;
  page: number;
  sort: string;
  statusFilter?: Status[];
  createdAtFilter?: {
    from: number;
    to: number;
  };
  typeFilter?: Category[];
  viewMode: string;
  //
  findStatus: string;
  showFilterByStatusModal: boolean;
  showFilterByDateModal: boolean;
  segmentSelected: string;
  count: number;
  showFilterByTypeModal: boolean;

  showMassiveSign: boolean;
  massiveSignStatus: string;
  signedCounter: number;
  createdByFilter: string;
  showFilterByCreatedModal: boolean;
  showModalToSignMassive: boolean;
}

const initialState = {
  documents: [],
  searchText: "",
  page: 1,
  sort: "createdAt DESC",
  statusFilter: [],
  createdAtFilter: {
    from: 0,
    to: 0,
  },
  typeFilter: [],
  viewMode: "grid",
  //
  findStatus: "",
  showFilterByStatusModal: false,
  showFilterByDateModal: false,
  segmentSelected: "pending",
  count: 0,
  showFilterByTypeModal: false,

  showMassiveSign: false,
  massiveSignStatus: "",
  signedCounter: 0,
  createdByFilter: "all",
  showFilterByCreatedModal: false,
  showModalToSignMassive: false,
} as SignerState;

// Mutaciones sincrónicas de datos
export const setSearchText = createAction<string>("SET_SEARCH_TEXT_SIGNER");
export const incrementPage = createAction("INCREMENT_PAGE_SIGNER");
export const resetDocumentList = createAction("RESET_DOCUMENT_LIST_SIGNER");
export const setSort = createAction<string>("SET_SORT_SIGNER");
export const setStatusFilter = createAction<any>("SET_STATUS_FILTER_SIGNER");
export const setCreatedAtFilter = createAction<any>("SET_CREATED_AT_FILTER_SIGNER");
export const setTypeFilter = createAction<any>("SET_TYPE_FILTER_SIGNER");
export const setViewMode = createAction<string>("SET_VIEW_MODE_SIGNER");
export const setFilterByStatusModal = createAction<boolean>("SET_FILTER_BY_STATUS_MODAL_SIGNER");
export const setFilterByDateModal = createAction<boolean>("SET_FILTER_BY_DATE_MODAL_SIGNER");
export const setSegmentSelected = createAction<string>("SET_SEGMENT_SELECTED_SIGNER");
export const setFilterByTypeModal = createAction<boolean>("SET_FILTER_BY_TYPE_MODAL_SIGNER");

export const setShowMassiveSign = createAction<boolean>("SET_MASSIVE_SIGN_SIGNER");
export const setCheck = createAction<Document>("SET_CHECK_SIGN_SIGNER");
export const setCheckAll = createAction("SET_CHECK_SIGN_ALL_SIGNER");
export const setUncheckAll = createAction("SET_UNCHECK_SIGN_ALL_SIGNER");
export const setMassiveSignStatus = createAction<string>("SET_MASSIVE_SIGN_STATUS_SIGNER");
export const resetMassiveSign = createAction("RESET_MASSIVE_SIGN_SIGNER");
export const setCreatedByFilter = createAction<string>("SET_CREATEDBY_FILTER_SIGNER");
export const setFilterByCreatedModal = createAction<any>("SET_FILTER_BY_CREATED_MODAL_SIGNER");
export const setShowModalToSignMassive = createAction<boolean>("SET_SHOW_MODAL_TO_SIGN_MASSIVE_SIGNER");

const documentsSignerListSlice = createSlice({
  name: "documentsSignerList",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(resetDocumentList, (state) => {
        state.documents = [];
        state.page = 1;
      })
      .addCase(setViewMode, (state, { payload }) => {
        state.viewMode = payload;
      })
      .addCase(incrementPage, (state) => {
        state.page += 1;
      })
      // Buscar documentos
      .addCase(fetchDocuments.pending, (state) => {
        state.findStatus = "pending";
        state.massiveSignStatus = "";
      })
      .addCase(fetchDocuments.fulfilled, (state, { payload }) => {
        if (payload.length > 1) {
          state.count = payload[1];
        }
        const documentList = payload[0].map((document: Document) => ({
          ...document,
          checked: false,
        }));
        state.documents.push(...documentList);
        state.findStatus = "fulfilled";
        state.signedCounter = 0;
      })
      .addCase(fetchDocuments.rejected, (state) => {
        state.findStatus = "rejected";
      })
      .addCase(setSearchText, (state, { payload = "" }) => {
        state.searchText = payload;
        state.page = 1;
        state.documents = [];
      })
      .addCase(setSort, (state, { payload }) => {
        state.sort = payload;
        state.page = 1;
        state.documents = [];
      })
      .addCase(setStatusFilter, (state, { payload }) => {
        state.statusFilter = payload;
        state.page = 1;
        state.documents = [];
      })
      .addCase(setCreatedAtFilter, (state, { payload }) => {
        state.createdAtFilter = payload;
        state.page = 1;
        state.documents = [];
      })
      .addCase(setTypeFilter, (state, { payload }) => {
        state.typeFilter = payload;
        state.page = 1;
        state.documents = [];
      })
      .addCase(setFilterByDateModal, (state, { payload }) => {
        state.showFilterByDateModal = payload;
      })
      .addCase(setFilterByStatusModal, (state, { payload }) => {
        state.showFilterByStatusModal = payload;
      })
      .addCase(setSegmentSelected, (state, { payload }) => {
        state.segmentSelected = payload;
        state.showMassiveSign = false;
      })
      .addCase(setFilterByTypeModal, (state, { payload }) => {
        state.showFilterByTypeModal = payload;
      })
      // Firma masiva
      .addCase(setShowMassiveSign, (state, { payload }) => {
        state.showMassiveSign = payload;
      })
      .addCase(setCheck, (state, { payload }) => {
        state.documents = state.documents.map((documentItem) => ({
          ...documentItem,
          checked: payload.id === documentItem.id ? !documentItem.checked : documentItem.checked,
        }));
      })
      .addCase(setCheckAll, (state) => {
        state.documents = state.documents.map((documentItem) => ({
          ...documentItem,
          checked: true,
        }));
      })
      .addCase(setUncheckAll, (state) => {
        state.documents = state.documents.map((documentItem) => ({
          ...documentItem,
          checked: false,
        }));
      })
      .addCase(setMassiveSignStatus, (state, { payload }) => {
        state.massiveSignStatus = payload;
      })
      .addCase(signDocument.fulfilled, (state) => {
        state.signedCounter = (state.signedCounter || 0) + 1;
      })
      .addCase(resetMassiveSign, (state) => {
        state.documents = [];
        state.signedCounter = 0;
        state.searchText = "";
      })
      .addCase(setCreatedByFilter, (state, { payload }) => {
        state.createdByFilter = payload;
        state.page = 1;
        state.documents = [];
      })
      .addCase(setFilterByCreatedModal, (state, { payload }) => {
        state.showFilterByCreatedModal = payload;
      })
      .addCase(setShowModalToSignMassive, (state, { payload }) => {
        state.showModalToSignMassive = payload;
      });
  },
});

export default documentsSignerListSlice.reducer;
